import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import type { Integration } from "@gtm/contracts";
import { TrashIcon } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useRouter } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";

export function DeleteIntegration({
  integration,
  disabled,
  onDeleted,
}: {
  integration: Integration;
  disabled?: boolean;
  onDeleted: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const client = useQueryClient();
  const router = useRouter();
  const { toast } = useToast();
  async function remove() {
    setBusy(true);
    try {
      await unwrap(
        apiClient().DELETE("/integrations/{id}", {
          params: { path: { id: integration.id } },
        }),
      );
      await client.cancelQueries({ queryKey: ["integrations"] });
      client.setQueryData<Integration[]>(["integrations"], (current) =>
        current?.filter((item) => item.id !== integration.id),
      );
      setConfirm(false);
      onDeleted();
      void client.invalidateQueries();
      router.refresh();
      toast({
        variant: ToastVariant.SUCCESS,
        header: "Integration deleted",
        description: integration.name,
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Button
        label="Delete integration"
        icon={TrashIcon}
        variant={ButtonVariant.TERTIARY}
        isDisabled={disabled || busy}
        onClick={() => setConfirm(true)}
      />
      <ConfirmAction
        isOpen={confirm}
        header={`Delete ${integration.name}?`}
        description={
          <>
            This connection will no longer be available for collection or delivery. Existing signals
            and history are kept. Work already in progress may finish.
          </>
        }
        label="Delete integration"
        isDestructive
        onOpenChange={(next) => {
          if (!next) (() => setConfirm(false))();
        }}
        onConfirm={async () => remove()}
      />
    </>
  );
}
